import { motion } from 'motion/react'
import {
    ArrowDownUp,
    DollarSign,
    Home,
    Plane,
    Receipt,
    Repeat,
    Utensils,
} from 'lucide-react'

import './auth-intro-scenes.css'

interface VirtualCardProps {
    delay?: number
    rotate?: number
    name?: string
    balance?: string
    expiry?: string
    className?: string
}

function VirtualCard({
    delay = 0,
    rotate = 0,
    name = 'Splitcard User',
    balance = '$0.00',
    expiry = '07/28',
    className = '',
}: VirtualCardProps) {
    return (
        <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1, rotate }}
            transition={{
                delay,
                duration: 0.65,
                ease: [0.22, 1, 0.36, 1],
            }}
            className={`auth-intro-card ${className}`}
        >
            <motion.div
                className="auth-intro-card-shine"
                animate={{ x: ['-100%', '200%'] }}
                transition={{
                    delay: delay + 1,
                    duration: 2,
                    ease: 'easeInOut',
                    repeat: Infinity,
                    repeatDelay: 3,
                }}
            />

            <div className="auth-intro-card-content">
                <div>
                    <div className="auth-intro-card-top-row">
                        <div className="auth-intro-card-brand-row">
                            <img
                                className="auth-intro-card-logo"
                                src="/splitcard/logo/white-and-blue-fallback.png"
                                alt="Splitcard"
                            />
                            <span className="auth-intro-card-bank-partner-label">Banking Partners:</span>
                        </div>
                        <div className="auth-intro-card-balance-wrap">
                            <span className="auth-intro-card-balance-label">Available</span>
                            <span className="auth-intro-card-balance">{balance}</span>
                        </div>
                    </div>

                    <div className="auth-intro-card-bank-row" aria-hidden>
                        <img className="auth-intro-card-bank-logo auth-intro-card-bank-logo--corpay" src="/corpay-logo.svg" alt="" />
                        <span className="auth-intro-card-bank-separator">&amp;</span>
                        <img className="auth-intro-card-bank-logo auth-intro-card-bank-logo--tera" src="/tera/name.svg" alt="" />
                    </div>
                </div>

                <div className="auth-intro-card-middle-row">
                    <span className="auth-intro-card-mask">•••• •••• •••• ••••</span>
                    <span className="auth-intro-card-expiry">{expiry}</span>
                </div>

                <div className="auth-intro-card-bottom-row">
                    <span className="auth-intro-card-name">{name}</span>
                    <img className="auth-intro-card-network" src="/payment-brands/mastercard-icon.svg" alt="Mastercard" />
                </div>
            </div>
        </motion.div>
    )
}

export function SplitBillScene() {
    return (
        <div className="auth-intro-scene">
            <motion.div
                className="auth-intro-scene-glow"
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />

            <div className="auth-intro-scene-card-wrap">
                <motion.div
                    initial={{ y: 24, opacity: 0, rotate: -6 }}
                    animate={{ y: 0, opacity: 1, rotate: -6 }}
                    transition={{ delay: 0.35, duration: 0.45 }}
                    className="auth-intro-scene-receipt"
                >
                    <Receipt className="auth-intro-scene-receipt-icon" />
                    <div className="auth-intro-scene-receipt-lines">
                        <span />
                        <span />
                        <span />
                    </div>
                </motion.div>

                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.4, type: 'spring', stiffness: 220, damping: 16 }}
                    className="auth-intro-scene-badge"
                >
                    <ArrowDownUp className="auth-intro-scene-badge-icon" />
                    <span>Split evenly</span>
                </motion.div>

                <VirtualCard delay={0.15} balance="$85.00" name="Dinner Group" className="auth-intro-scene-card" />
            </div>

            <div className="auth-intro-split-amounts" aria-hidden>
                <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.4, type: 'spring' }}
                    className="auth-intro-pill"
                >
                    <span className="auth-intro-pill-avatar auth-intro-pill-avatar--blue">JD</span>
                    <span>$42.50</span>
                </motion.div>

                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.15, type: 'spring' }}
                    className="auth-intro-split-divider"
                />

                <motion.div
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 1.0, duration: 0.4, type: 'spring' }}
                    className="auth-intro-pill"
                >
                    <span className="auth-intro-pill-avatar auth-intro-pill-avatar--green">MK</span>
                    <span>$42.50</span>
                </motion.div>
            </div>
        </div>
    )
}

export function TrackExpensesScene() {
    const expenses = [
        {
            icon: <Plane className="auth-intro-expense-icon" />,
            label: 'Flights',
            amount: '$380',
            className: 'auth-intro-expense-chip--sky',
        },
        {
            icon: <Utensils className="auth-intro-expense-icon" />,
            label: 'Dinner',
            amount: '$96',
            className: 'auth-intro-expense-chip--amber',
        },
        {
            icon: <Home className="auth-intro-expense-icon" />,
            label: 'Hotel',
            amount: '$240',
            className: 'auth-intro-expense-chip--violet',
        },
    ]

    return (
        <div className="auth-intro-scene">
            <motion.div
                className="auth-intro-scene-glow"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
            />

            <VirtualCard delay={0.15} rotate={-4} balance="$716.00" name="Bali Trip" className="auth-intro-scene-card" />

            <div className="auth-intro-expense-list" aria-hidden>
                {expenses.map((expense, index) => (
                    <motion.div
                        key={expense.label}
                        initial={{ x: 25, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.8 + index * 0.2, duration: 0.35, type: 'spring' }}
                        className="auth-intro-expense-row"
                    >
                        <div className={`auth-intro-expense-chip ${expense.className}`}>{expense.icon}</div>
                        <span className="auth-intro-expense-label">{expense.label}</span>
                        <span className="auth-intro-expense-amount">{expense.amount}</span>
                    </motion.div>
                ))}
            </div>

            <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.45, duration: 0.4 }}
                className="auth-intro-avatars"
                aria-hidden
            >
                <div className="auth-intro-avatar-stack">
                    {['JD', 'MK', 'AS', '+2'].map((name, index) => (
                        <motion.div
                            key={name}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 1.55 + index * 0.1, type: 'spring' }}
                            className={`auth-intro-avatar ${index === 0
                                ? 'auth-intro-avatar--blue'
                                : index === 1
                                    ? 'auth-intro-avatar--green'
                                    : index === 2
                                        ? 'auth-intro-avatar--amber'
                                        : 'auth-intro-avatar--muted'}`}
                        >
                            {name}
                        </motion.div>
                    ))}
                </div>
                <span className="auth-intro-avatars-label">5 members</span>
            </motion.div>
        </div>
    )
}

export function SharedExpensesScene() {
    const recurring = [
        { label: 'Rent', amount: '$875', day: '1st', active: true },
        { label: 'Electricity', amount: '$62', day: '15th', active: true },
        { label: 'Wi-Fi', amount: '$30', day: '20th', active: false },
    ]

    return (
        <div className="auth-intro-scene">
            <motion.div
                className="auth-intro-scene-glow"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 4.3, repeat: Infinity, ease: 'easeInOut' }}
            />

            <div className="auth-intro-scene-card-wrap">
                <VirtualCard delay={0.15} balance="$967.00" name="Apartment 4B" className="auth-intro-scene-card" />
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.6, type: 'spring', stiffness: 200, damping: 12 }}
                    className="auth-intro-scene-check"
                    aria-hidden
                >
                    <motion.svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                        <motion.path
                            d="M5 10.5L8.5 14L15 7"
                            stroke="white"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ delay: 1.8, duration: 0.5 }}
                        />
                    </motion.svg>
                </motion.div>
            </div>

            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.45 }}
                className="auth-intro-recurring"
                aria-hidden
            >
                <div className="auth-intro-recurring-header">
                    <Repeat className="auth-intro-recurring-icon" />
                    <span>Auto-splits</span>
                </div>

                {recurring.map((item, index) => (
                    <motion.div
                        key={item.label}
                        initial={{ x: -15, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 1 + index * 0.14, duration: 0.3 }}
                        className="auth-intro-recurring-row"
                    >
                        <div className="auth-intro-recurring-left">
                            <span className={`auth-intro-recurring-dot ${item.active ? 'auth-intro-recurring-dot--active' : ''}`} />
                            <span>{item.label}</span>
                        </div>
                        <div className="auth-intro-recurring-right">
                            <span>{item.day}</span>
                            <span>{item.amount}</span>
                        </div>
                    </motion.div>
                ))}
            </motion.div>
        </div>
    )
}

export function CollectMoneyScene() {
    const contributors = [
        { name: 'Alex', amount: '$25', avatar: 'AS', className: 'auth-intro-avatar--blue' },
        { name: 'Maya', amount: '$25', avatar: 'MK', className: 'auth-intro-avatar--green' },
        { name: 'Jordan', amount: '$30', avatar: 'JD', className: 'auth-intro-avatar--amber' },
    ]

    return (
        <div className="auth-intro-scene">
            <motion.div
                className="auth-intro-scene-glow"
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />

            <div className="auth-intro-contributors" aria-hidden>
                {contributors.map((person, index) => (
                    <motion.div
                        key={person.name}
                        initial={{ y: -18, opacity: 0, scale: 0.8 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        transition={{ delay: 0.75 + index * 0.2, duration: 0.45, type: 'spring' }}
                        className="auth-intro-contributor"
                    >
                        <span className={`auth-intro-pill-avatar ${person.className}`}>{person.avatar}</span>
                        <span className="auth-intro-contributor-copy">
                            <span>{person.name}</span>
                            <span>+{person.amount}</span>
                        </span>
                    </motion.div>
                ))}
            </div>

            <div className="auth-intro-flow" aria-hidden>
                {[0, 1, 2].map((index) => (
                    <motion.div
                        key={index}
                        className="auth-intro-flow-line"
                        animate={{ opacity: [0.2, 0.8, 0.2], y: [0, 4, 0] }}
                        transition={{ delay: 1.3 + index * 0.12, duration: 1.2, repeat: Infinity, repeatDelay: 1.3 }}
                    />
                ))}
            </div>

            <VirtualCard delay={0.15} balance="$80.00" name="Birthday Party" className="auth-intro-scene-card" />

            <motion.div
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.55, duration: 0.45 }}
                className="auth-intro-pool-total"
                aria-hidden
            >
                <div className="auth-intro-pool-icon-wrap">
                    <DollarSign className="auth-intro-pool-icon" />
                </div>

                <div className="auth-intro-pool-copy">
                    <span>Group Total</span>
                    <span className="auth-intro-pool-amount-wrap">
                        <motion.span
                            className="auth-intro-pool-amount"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.9, duration: 0.3 }}
                        >
                            $80
                        </motion.span>
                        <span className="auth-intro-pool-goal">/ $100</span>
                    </span>
                </div>

                <div className="auth-intro-pool-progress">
                    <motion.span
                        className="auth-intro-pool-progress-fill"
                        initial={{ width: '0%' }}
                        animate={{ width: '80%' }}
                        transition={{ delay: 1.9, duration: 0.8, ease: 'easeOut' }}
                    />
                </div>
            </motion.div>
        </div>
    )
}
